// GST split, mirrored from the backend (common/utils/document-totals.util.ts)
// so the forms can preview what the server will store. The server decides;
// this only shows it.

export type TaxSupply = 'INTRA_STATE' | 'INTER_STATE';
/** Form value for "no override": the radix Select can't hold an empty string. */
export type TaxSupplyChoice = 'AUTO' | TaxSupply;

/**
 * A document's own tax lines. "AUTO" follows the organisation: one line in
 * SINGLE mode, picked from the client's state in SPLIT mode.
 */
export function taxSupplyOptions(taxMode?: string | null, taxLabel = 'Tax'): Array<{ label: string; value: TaxSupplyChoice }> {
  return [
    { label: taxMode === 'SPLIT' ? 'Automatic — from the client’s state' : `One line — ${taxLabel}`, value: 'AUTO' },
    { label: 'CGST + SGST — client in our state', value: 'INTRA_STATE' },
    { label: 'IGST — another state or abroad', value: 'INTER_STATE' },
  ];
}

const normalise = (v?: string | null) => (v ?? '').toLowerCase().replace(/[^a-z]/g, '');
const INDIA = new Set(['india', 'in', 'ind', 'bharat']);

export function resolveTaxSupply(
  choice: TaxSupplyChoice | string | null | undefined,
  place: { billingState?: string | null; billingCountry?: string | null },
  orgState?: string | null,
): TaxSupply {
  if (choice === 'INTRA_STATE' || choice === 'INTER_STATE') return choice;
  const country = normalise(place.billingCountry);
  if (country && !INDIA.has(country)) return 'INTER_STATE';
  const theirs = normalise(place.billingState);
  const ours = normalise(orgState);
  if (!theirs || !ours) return 'INTRA_STATE';
  return theirs === ours ? 'INTRA_STATE' : 'INTER_STATE';
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The tax lines a document prints: CGST + SGST, IGST, or one line under the org's label. */
export function taxLines(
  doc: { taxRate: number; taxAmount: number; cgstRate?: number; cgstAmount?: number; sgstRate?: number; sgstAmount?: number; igstRate?: number; igstAmount?: number },
  taxLabel = 'Tax',
): Array<{ label: string; amount: number }> {
  const split = [
    { name: 'CGST', rate: Number(doc.cgstRate) || 0, amount: Number(doc.cgstAmount) || 0 },
    { name: 'SGST', rate: Number(doc.sgstRate) || 0, amount: Number(doc.sgstAmount) || 0 },
    { name: 'IGST', rate: Number(doc.igstRate) || 0, amount: Number(doc.igstAmount) || 0 },
  ].filter((l) => l.amount > 0);
  if (split.length) return split.map((l) => ({ label: `${l.name} (${l.rate}%)`, amount: l.amount }));
  if (Number(doc.taxAmount) > 0) return [{ label: `${taxLabel} (${Number(doc.taxRate)}%)`, amount: Number(doc.taxAmount) }];
  return [];
}

/** Live preview of the split for a form that hasn't been saved yet. */
export function previewTaxLines(
  taxRate: number,
  taxAmount: number,
  settings: { taxMode?: string | null; taxLabel?: string | null; state?: string | null } | undefined,
  choice: TaxSupplyChoice | undefined,
  place: { billingState?: string | null; billingCountry?: string | null },
) {
  const label = settings?.taxLabel || 'Tax';
  const chosen = choice === 'INTRA_STATE' || choice === 'INTER_STATE';
  if (!(taxRate > 0) || (!chosen && settings?.taxMode !== 'SPLIT')) return taxLines({ taxRate, taxAmount }, label);
  const tax = round2(taxAmount);
  if (resolveTaxSupply(choice, place, settings?.state) === 'INTER_STATE') {
    return taxLines({ taxRate, taxAmount, igstRate: taxRate, igstAmount: tax }, label);
  }
  const cgstRate = round2(taxRate / 2);
  const cgstAmount = round2(tax / 2);
  return taxLines({ taxRate, taxAmount, cgstRate, cgstAmount, sgstRate: round2(taxRate - cgstRate), sgstAmount: round2(tax - cgstAmount) }, label);
}

/** Indian GSTIN: 2-digit state code, PAN, entity number, 'Z', checksum. */
export const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export function looksLikeGstin(value?: string | null) {
  return GSTIN_PATTERN.test((value ?? '').trim().toUpperCase());
}
